import { Component, lazy, Suspense, useState, type ReactNode } from 'react';
import { LoaderCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ErrorState } from '@resource-kit/ui/react';
import type { RoutePlanningMapProps } from './RoutePlanningMap';

/**
 * The one way into the route map (#2679). maplibre is large, and the desktop
 * route tab and the phone driver map share one module graph with the entry
 * page, so the map module loads only when a map is on screen. Keep this the
 * only non-type import of `./RoutePlanningMap` (pinned by
 * `__tests__/maplibreLazyBoundary.test.ts`).
 */
function loadMapChunk() {
  return lazy(() => import('./RoutePlanningMap').then((m) => ({ default: m.RoutePlanningMap })));
}

// `lazy` keeps a failed load for good, so a retry swaps in a fresh one. The
// swap is shared: a map mounted later starts from the last attempt.
let mapChunk = loadMapChunk();

function MapLoading() {
  const { t } = useTranslation();
  return (
    <div
      role="status"
      className="flex h-full min-h-80 items-center justify-center gap-2 text-sm text-muted-foreground"
    >
      <LoaderCircle className="h-5 w-5 animate-spin" aria-hidden="true" />
      {t('routePlanning.loading')}
    </div>
  );
}

function MapLoadFailed({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <ErrorState
      message={t('error.loadFailed')}
      action={
        <button
          type="button"
          onClick={onRetry}
          className="inline-flex h-11 items-center rounded-lg border border-border bg-card px-3.5 font-medium text-foreground"
        >
          {t('ui.retry')}
        </button>
      }
    />
  );
}

/**
 * A tab left open across a deploy asks for a hashed chunk that is gone
 * (#2246). That failure stays in the map area instead of reaching the app's
 * global error page.
 */
class MapChunkBoundary extends Component<
  { children: ReactNode; onRetry: () => void },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  render() {
    return this.state.failed ? <MapLoadFailed onRetry={this.props.onRetry} /> : this.props.children;
  }
}

export function LazyRoutePlanningMap(props: RoutePlanningMapProps) {
  const [attempt, setAttempt] = useState({ count: 0, Chunk: mapChunk });
  // A new boundary key clears the error; the fresh chunk loads the map again.
  const retry = () => {
    mapChunk = loadMapChunk();
    setAttempt((prev) => ({ count: prev.count + 1, Chunk: mapChunk }));
  };
  const { Chunk } = attempt;
  return (
    <MapChunkBoundary key={attempt.count} onRetry={retry}>
      <Suspense fallback={<MapLoading />}>
        <Chunk {...props} />
      </Suspense>
    </MapChunkBoundary>
  );
}
